import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import { createICSpicyActor } from '../services/icspicy';
import { Principal } from '@dfinity/principal';
import { useAuth } from '../auth/useAuth';

interface MultichainDeployment {
  token_id: bigint;
  evm?: {
    contract_address: string;
    chain_id: number;
    transaction_hash?: string;
    status: { Deploying?: null; Deployed?: null; Failed?: null };
    deployed_at?: bigint;
  };
  solana?: {
    mint_address: string;
    transaction_signature?: string;
    status: { Deploying?: null; Deployed?: null; Failed?: null };
    deployed_at?: bigint;
  };
  bitcoin?: {
    inscription_id: string;
    transaction_id?: string;
    status: { Deploying?: null; Deployed?: null; Failed?: null };
    deployed_at?: bigint;
  };
}

export default function MintPage() {
  const { agent, principal, isAuthenticated, loginWithII } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [minting, setMinting] = useState(false);
  const [batchMinting, setBatchMinting] = useState(false);
  const [batchQuantity, setBatchQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mintedTokenId, setMintedTokenId] = useState<bigint | null>(null);
  const [deploymentStatus, setDeploymentStatus] = useState<MultichainDeployment | null>(null);
  const [checkingDeployment, setCheckingDeployment] = useState(false);

  useEffect(() => {
    const loadRole = async () => {
      if (!agent || !isAuthenticated || !principal) return;
      try {
        const actor = await createICSpicyActor(agent);
        const role = await actor.get_user_role_query(principal);
        if (role && role.length > 0) {
          setUserRole(role[0].hasOwnProperty('Admin') ? 'Admin' : 'User');
        } else {
          setUserRole(null);
        }
      } catch (err) {
        console.error('Failed to load role:', err);
      }
    };
    loadRole();
  }, [agent, isAuthenticated, principal]);

  const handleConnect = async () => {
    try {
      await loginWithII();
    } catch (err: any) {
      setError(err.message || 'Failed to connect');
    }
  };

  const handleMint = async () => {
    if (!agent || !isAuthenticated) {
      setError('Please connect wallet first');
      return;
    }

    setMinting(true);
    setError(null);
    setSuccess(null);

    try {
      const actor = await createICSpicyActor(agent);
      const recipient = recipientAddress 
        ? Principal.fromText(recipientAddress)
        : undefined;
      
      const result = await actor.mint(recipient ? [recipient] : []);
      
      if (result.success) {
        const tokenId = result.token_ids[0];
        setMintedTokenId(tokenId);
        setSuccess(`Minted NFT! Token ID: ${tokenId}`);
        setRecipientAddress('');
        
        // Check deployment status
        setCheckingDeployment(true);
        try {
          const deployment = await actor.get_multichain_deployment(tokenId);
          setDeploymentStatus(deployment || null);
        } catch (err) {
          console.error('Failed to get deployment status:', err);
        } finally {
          setCheckingDeployment(false);
        }
      } else {
        setError('Minting failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mint NFT');
    } finally {
      setMinting(false);
    }
  };

  const handleBatchMint = async () => {
    if (!agent || !isAuthenticated || userRole !== 'Admin') {
      setError('Admin access required for batch minting');
      return;
    }

    setBatchMinting(true);
    setError(null);
    setSuccess(null);

    try {
      const actor = await createICSpicyActor(agent);
      const recipient = recipientAddress 
        ? Principal.fromText(recipientAddress)
        : undefined;
      
      const result = await actor.batch_mint({
        recipient: recipient ? [recipient] : [],
        quantity: BigInt(batchQuantity),
      });
      
      if (result.success) {
        setSuccess(`Batch minted ${result.token_ids.length} NFTs! Token IDs: ${result.token_ids.join(', ')}`);
        setRecipientAddress('');
        setBatchQuantity(1);
      } else {
        setError('Batch minting failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to batch mint');
    } finally {
      setBatchMinting(false);
    }
  };

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <div className="glass-dark rounded-2xl p-8 mb-6">
            <h1 className="text-4xl font-bold mb-4 text-gold">Mint IC Spicy NFT</h1>
            
            {!isAuthenticated ? (
              <button
                onClick={handleConnect}
                className="bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-all"
              >
                Connect Wallet
              </button>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-300 mb-2">Connected as:</p>
                  <p className="text-sm font-mono break-all bg-black/30 p-2 rounded">
                    {principal?.toText()}
                  </p>
                  {userRole && (
                    <p className="text-gold mt-2">Role: {userRole}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-gray-300 mb-2">
                    Recipient Wallet Address (optional)
                  </label>
                  <input
                    type="text"
                    value={recipientAddress}
                    onChange={(e) => setRecipientAddress(e.target.value)}
                    placeholder="Leave empty to mint to your wallet"
                    className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Enter principal ID in Plug Wallet format
                  </p>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={handleMint}
                    disabled={minting}
                    className="w-full bg-gold hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-all"
                  >
                    {minting ? 'Minting...' : 'Mint Single NFT'}
                  </button>

                  {userRole === 'Admin' && (
                    <div className="border-t border-gray-600 pt-4">
                      <h3 className="text-xl font-semibold mb-4 text-gold">Batch Mint (Admin Only)</h3>
                      <div className="mb-4">
                        <label className="block text-gray-300 mb-2">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={batchQuantity}
                          onChange={(e) => setBatchQuantity(parseInt(e.target.value) || 1)}
                          className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                        />
                      </div>
                      <button
                        onClick={handleBatchMint}
                        disabled={batchMinting}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-all"
                      >
                        {batchMinting ? 'Batch Minting...' : `Batch Mint ${batchQuantity} NFTs`}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200 mb-4"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-green-900/30 border border-green-500 rounded-lg text-green-200 mb-4"
            >
              {success}
            </motion.div>
          )}

          {mintedTokenId !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-dark rounded-2xl p-6 mb-4"
            >
              <h2 className="text-2xl font-bold mb-4 text-gold">Multichain Deployment Status</h2>
              
              {checkingDeployment ? (
                <p className="text-gray-300">Checking deployment status...</p>
              ) : deploymentStatus ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                    <span className="text-gray-300 font-semibold">ICP (Native)</span>
                    <span className="text-green-400">✓ Deployed</span>
                  </div>
                  
                  {deploymentStatus.evm && (
                    <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                      <div>
                        <span className="text-gray-300 font-semibold">Ethereum</span>
                        <p className="text-xs text-gray-400 mt-1 font-mono">{deploymentStatus.evm.contract_address}</p>
                      </div>
                      <span className={`${
                        deploymentStatus.evm.status.Deploying ? 'text-yellow-400' :
                        deploymentStatus.evm.status.Deployed ? 'text-green-400' :
                        'text-red-400'
                      }`}>
                        {deploymentStatus.evm.status.Deploying ? '⏳ Deploying' :
                         deploymentStatus.evm.status.Deployed ? '✓ Deployed' :
                         '✗ Failed'}
                      </span>
                    </div>
                  )}
                  
                  {deploymentStatus.solana && (
                    <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                      <div>
                        <span className="text-gray-300 font-semibold">Solana</span>
                        <p className="text-xs text-gray-400 mt-1 font-mono">{deploymentStatus.solana.mint_address}</p>
                      </div>
                      <span className={`${
                        deploymentStatus.solana.status.Deploying ? 'text-yellow-400' :
                        deploymentStatus.solana.status.Deployed ? 'text-green-400' :
                        'text-red-400'
                      }`}>
                        {deploymentStatus.solana.status.Deploying ? '⏳ Deploying' :
                         deploymentStatus.solana.status.Deployed ? '✓ Deployed' :
                         '✗ Failed'}
                      </span>
                    </div>
                  )}
                  
                  {deploymentStatus.bitcoin && (
                    <div className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                      <div>
                        <span className="text-gray-300 font-semibold">Bitcoin</span>
                        <p className="text-xs text-gray-400 mt-1 font-mono">{deploymentStatus.bitcoin.inscription_id}</p>
                      </div>
                      <span className={`${
                        deploymentStatus.bitcoin.status.Deploying ? 'text-yellow-400' :
                        deploymentStatus.bitcoin.status.Deployed ? 'text-green-400' :
                        'text-red-400'
                      }`}>
                        {deploymentStatus.bitcoin.status.Deploying ? '⏳ Deploying' :
                         deploymentStatus.bitcoin.status.Deployed ? '✓ Deployed' :
                         '✗ Failed'}
                      </span>
                    </div>
                  )}
                  
                  {!deploymentStatus.evm && !deploymentStatus.solana && !deploymentStatus.bitcoin && (
                    <p className="text-gray-400 text-sm">Multichain deployment is being initiated...</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">Deployment status not available yet. Please check your wallet page.</p>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </BlacksmithWorkshop>
  );
}






